"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useMemo } from "react";
import PrimaryButton from "@/components/utils/button/PrimaryButton";
import InfoButton from "@/components/utils/button/InfoButton";
import ClosedButton from "@/components/utils/button/ClosedButton";
import ClockIcon from "@/components/utils/icons/ClockIcon";
import CalendarClosedIcon from "@/components/utils/icons/CalendarClosedIcon";
import LimitIcon from "@/components/utils/icons/LimitIcon";
import PermissionIcon from "@/components/utils/icons/PermissionIcon";
import type { ApiWave } from "@/generated/models/ApiWave";
import { useWave, SubmissionStatus } from "@/hooks/useWave";
import { useCountdown } from "@/hooks/useCountdown";
interface MyStreamWaveTabsMemeSubmitProps {
  readonly wave: ApiWave;
  readonly handleMemesSubmit: () => void;
}
const HEADER_ACTION_BUTTON_CLASS = "tw-max-w-[11.5rem] tw-whitespace-nowrap lg:tw-max-w-[13.5rem] xl:tw-max-w-none";
const HEADER_ACTION_BUTTON_TEXT_CLASS = "tw-min-w-0 tw-truncate";
const ACTIVE_LABEL_SUBMIT_MEME = "Submit Meme";
const getActiveLabels = ({
  canSubmit,
  isEndingVerySoon,
  isEndingHighlyUrgent,
  isEndingSoon
}: {
  canSubmit: boolean;
  isEndingVerySoon: boolean;
  isEndingHighlyUrgent: boolean;
  isEndingSoon: boolean;
}): {
  activeLabelFull: string;
  activeLabelCompact: string;
} => {
  if (!canSubmit) {
    return {
      activeLabelFull: "Submit Work to The Memes",
      activeLabelCompact: "Submit Work"
    };
  }
  if (isEndingVerySoon) {
    return {
      activeLabelFull: ACTIVE_LABEL_SUBMIT_MEME,
      activeLabelCompact: ACTIVE_LABEL_SUBMIT_MEME
    };
  }
  if (isEndingHighlyUrgent) {
    return {
      activeLabelFull: ACTIVE_LABEL_SUBMIT_MEME,
      activeLabelCompact: ACTIVE_LABEL_SUBMIT_MEME
    };
  }
  if (isEndingSoon) {
    return {
      activeLabelFull: "Submit Meme (Closes Soon!)",
      activeLabelCompact: ACTIVE_LABEL_SUBMIT_MEME
    };
  }
  return {
    activeLabelFull: "Submit Work to The Memes",
    activeLabelCompact: "Submit Work"
  };
};

/**
 * Button component for The Memes submissions with multiple states:
 * - Coming soon (blue info button with countdown)
 * - Active (green primary button)
 *   - With submission count indicator when limited submissions remaining
 *   - With urgency indicator when submission period is ending soon
 * - Maximum submissions reached (blue info button, disabled)
 * - Not eligible (blue info button, disabled, with permission icon)
 * - Closed (gray disabled button)
 *
 * Uses useWave hook to determine submission status, eligibility,
 * time windows, and submission limits
 */
const MyStreamWaveTabsMemeSubmit: React.FC<MyStreamWaveTabsMemeSubmitProps> = t0 => {
  const $ = _c(70);
  const {
    wave,
    handleMemesSubmit
  } = t0;
  const waveInfo = useWave(wave);
  const targetTime = waveInfo?.participation.status === SubmissionStatus.NOT_STARTED ? waveInfo?.participation.startTime : null;
  const countdown = useCountdown(targetTime);
  const endTimeForCountdown = waveInfo?.participation.endTime ?? null;
  const endingCountdown = useCountdown(endTimeForCountdown);
  waveInfo?.participation.canSubmitNow;
  const canSubmit = waveInfo?.participation.canSubmitNow ?? false;
  if (!waveInfo) {
    return null;
  }
  const submissionStatus = waveInfo.participation.status;
  const isClosed = submissionStatus === SubmissionStatus.ENDED;
  const isComingSoon = submissionStatus === SubmissionStatus.NOT_STARTED;
  const openingTime = waveInfo.participation.startTime;
  const closingTime = waveInfo.participation.endTime;
  if (isClosed) {
    let t1;
    if ($[0] !== closingTime) {
      t1 = closingTime ? `Submissions closed on ${new Date(closingTime).toLocaleString()}` : "Submissions are closed";
      $[0] = closingTime;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    const tooltipText = t1;
    let t2;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <CalendarClosedIcon className="tw-h-5 tw-w-5 tw-flex-shrink-0" />;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    let t3;
    if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <span className={HEADER_ACTION_BUTTON_TEXT_CLASS}><span className="xl:tw-hidden">Closed</span><span className="tw-hidden xl:tw-inline">Submissions Closed</span></span>;
      $[3] = t3;
    } else {
      t3 = $[3];
    }
    let t4;
    if ($[4] !== tooltipText) {
      t4 = <ClosedButton title={tooltipText} fullWidth={false} className={HEADER_ACTION_BUTTON_CLASS}>{t2}{t3}</ClosedButton>;
      $[4] = tooltipText;
      $[5] = t4;
    } else {
      t4 = $[5];
    }
    return t4;
  }
  if (isComingSoon) {
    let t1;
    if ($[6] !== openingTime) {
      t1 = openingTime ? `Submissions open on ${new Date(openingTime).toLocaleString()}` : "Submissions will open soon";
      $[6] = openingTime;
      $[7] = t1;
    } else {
      t1 = $[7];
    }
    const tooltipText_0 = t1;
    let t2;
    if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <ClockIcon className="tw-h-5 tw-w-5 tw-flex-shrink-0" />;
      $[8] = t2;
    } else {
      t2 = $[8];
    }
    let t3;
    if ($[9] !== countdown) {
      t3 = <span className={HEADER_ACTION_BUTTON_TEXT_CLASS}><span className="xl:tw-hidden">Opens {countdown}</span><span className="tw-hidden xl:tw-inline">Submissions Open {countdown}</span></span>;
      $[9] = countdown;
      $[10] = t3;
    } else {
      t3 = $[10];
    }
    let t4;
    if ($[11] !== t3 || $[12] !== tooltipText_0) {
      t4 = <InfoButton disabled={true} title={tooltipText_0} variant="info" fullWidth={false} className={HEADER_ACTION_BUTTON_CLASS}>{t2}{t3}</InfoButton>;
      $[11] = t3;
      $[12] = tooltipText_0;
      $[13] = t4;
    } else {
      t4 = $[13];
    }
    return t4;
  }
  if (waveInfo.participation.isEligible === false) {
    let t1;
    if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <PermissionIcon className="tw-h-5 tw-w-5 tw-flex-shrink-0" />;
      $[14] = t1;
    } else {
      t1 = $[14];
    }
    let t2;
    if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <InfoButton disabled={true} title="You don't have permission to submit to this wave" variant="muted" fullWidth={false} className={HEADER_ACTION_BUTTON_CLASS}>{t1}<span className={HEADER_ACTION_BUTTON_TEXT_CLASS}><span className="xl:tw-hidden">Not Eligible</span><span className="tw-hidden xl:tw-inline">Not Eligible to Submit</span></span></InfoButton>;
      $[15] = t2;
    } else {
      t2 = $[15];
    }
    return t2;
  }
  if (waveInfo.participation.hasReachedLimit) {
    const maxSubmissions = waveInfo.participation.maxSubmissions ?? "?";
    const submissionText = maxSubmissions === 1 ? "1 submission" : `${maxSubmissions} submissions`;
    const tooltipText_2 = `You have already submitted the maximum allowed (${submissionText})`;
    let t1;
    if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <LimitIcon className="tw-h-5 tw-w-5 tw-flex-shrink-0" />;
      $[16] = t1;
    } else {
      t1 = $[16];
    }
    let t2;
    if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <span className="xl:tw-hidden">Limit Reached</span>;
      $[17] = t2;
    } else {
      t2 = $[17];
    }
    const t3 = maxSubmissions === 1 ? "Submission Limit Reached (1)" : `Submission Limit Reached (${maxSubmissions})`;
    let t4;
    if ($[18] !== t3) {
      t4 = <span className={HEADER_ACTION_BUTTON_TEXT_CLASS}>{t2}<span className="tw-hidden xl:tw-inline">{t3}</span></span>;
      $[18] = t3;
      $[19] = t4;
    } else {
      t4 = $[19];
    }
    let t5;
    if ($[20] !== t4 || $[21] !== tooltipText_2) {
      t5 = <InfoButton disabled={true} title={tooltipText_2} variant="muted" fullWidth={false} className={HEADER_ACTION_BUTTON_CLASS}>{t1}{t4}</InfoButton>;
      $[20] = t4;
      $[21] = tooltipText_2;
      $[22] = t5;
    } else {
      t5 = $[22];
    }
    return t5;
  }
  const hasLimitedRemaining = waveInfo.participation.remainingSubmissions !== null && waveInfo.participation.remainingSubmissions <= 3 && waveInfo.participation.remainingSubmissions > 0;
  const now = Date.now();
  const endTime = waveInfo.participation.endTime;
  const timeRemaining = endTime ? endTime - now : null;
  const isEndingVerySoon = waveInfo.participation.isWithinPeriod && timeRemaining !== null && timeRemaining < 3600000 && timeRemaining > 0;
  const isEndingHighlyUrgent = waveInfo.participation.isWithinPeriod && timeRemaining !== null && timeRemaining < 21600000 && timeRemaining > 0;
  const isEndingSoon = waveInfo.participation.isWithinPeriod && timeRemaining !== null && timeRemaining < 86400000 && timeRemaining > 0;
  let t1;
  if ($[23] !== canSubmit || $[24] !== endTime || $[25] !== hasLimitedRemaining || $[26] !== isEndingHighlyUrgent || $[27] !== isEndingSoon || $[28] !== isEndingVerySoon || $[29] !== waveInfo.participation.remainingSubmissions) {
    t1 = canSubmit ? hasLimitedRemaining ? `You have ${waveInfo.participation.remainingSubmissions} submission${waveInfo.participation.remainingSubmissions === 1 ? "" : "s"} remaining` : isEndingVerySoon ? `URGENT: Submissions closing in less than an hour! Ends at ${new Date(endTime).toLocaleString()}` : isEndingHighlyUrgent ? `Submissions closing in a few hours! Ends at ${new Date(endTime).toLocaleString()}` : isEndingSoon ? `Submissions closing soon! Ends on ${new Date(endTime).toLocaleString()}` : "Submit your art for The Memes" : "You cannot submit at this time";
    $[23] = canSubmit;
    $[24] = endTime;
    $[25] = hasLimitedRemaining;
    $[26] = isEndingHighlyUrgent;
    $[27] = isEndingSoon;
    $[28] = isEndingVerySoon;
    $[29] = waveInfo.participation.remainingSubmissions;
    $[30] = t1;
  } else {
    t1 = $[30];
  }
  const tooltipText_3 = t1;
  let t2;
  if ($[31] !== canSubmit || $[32] !== isEndingHighlyUrgent || $[33] !== isEndingSoon || $[34] !== isEndingVerySoon) {
    t2 = getActiveLabels({
      canSubmit,
      isEndingVerySoon,
      isEndingHighlyUrgent,
      isEndingSoon
    });
    $[31] = canSubmit;
    $[32] = isEndingHighlyUrgent;
    $[33] = isEndingSoon;
    $[34] = isEndingVerySoon;
    $[35] = t2;
  } else {
    t2 = $[35];
  }
  const {
    activeLabelFull,
    activeLabelCompact
  } = t2;
  const showSplitActiveLabels = activeLabelCompact !== activeLabelFull;
  const t3 = !canSubmit;
  let t4;
  if ($[36] !== canSubmit || $[37] !== isEndingHighlyUrgent || $[38] !== isEndingSoon || $[39] !== isEndingVerySoon) {
    t4 = isEndingVerySoon && canSubmit ? <ClockIcon className="tw-text-red-500 tw-h-5 tw-w-5 tw-flex-shrink-0 tw-animate-pulse" /> : isEndingHighlyUrgent && canSubmit ? <ClockIcon className="tw-h-5 tw-w-5 tw-flex-shrink-0 tw-animate-pulse tw-text-amber-500" /> : isEndingSoon && canSubmit ? <ClockIcon className="tw-h-5 tw-w-5 tw-flex-shrink-0 tw-text-amber-400" /> : <svg className="tw-h-5 tw-w-5 tw-flex-shrink-0" viewBox="0 0 24 24" fill="none" aria-hidden="true" xmlns="http://www.w3.org/2000/svg"><path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /><path d="M7.5 12H16.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /><path d="M12 7.5V16.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>;
    $[36] = canSubmit;
    $[37] = isEndingHighlyUrgent;
    $[38] = isEndingSoon;
    $[39] = isEndingVerySoon;
    $[40] = t4;
  } else {
    t4 = $[40];
  }
  let t5;
  if ($[41] !== activeLabelCompact || $[42] !== activeLabelFull || $[43] !== showSplitActiveLabels) {
    t5 = showSplitActiveLabels ? <><span className="xl:tw-hidden">{activeLabelCompact}</span><span className="tw-hidden xl:tw-inline">{activeLabelFull}</span></> : <span>{activeLabelFull}</span>;
    $[41] = activeLabelCompact;
    $[42] = activeLabelFull;
    $[43] = showSplitActiveLabels;
    $[44] = t5;
  } else {
    t5 = $[44];
  }
  let t6;
  if ($[45] !== canSubmit || $[46] !== endingCountdown || $[47] !== isEndingVerySoon) {
    t6 = isEndingVerySoon && canSubmit && <span className="tw-bg-red-600 tw-hidden tw-animate-pulse tw-items-center tw-rounded-full tw-border tw-border-white/30 tw-px-1.5 tw-py-0.5 tw-text-xs tw-font-medium tw-text-white xl:tw-inline-flex">Closing {endingCountdown}!</span>;
    $[45] = canSubmit;
    $[46] = endingCountdown;
    $[47] = isEndingVerySoon;
    $[48] = t6;
  } else {
    t6 = $[48];
  }
  let t7;
  if ($[49] !== canSubmit || $[50] !== endingCountdown || $[51] !== isEndingHighlyUrgent || $[52] !== isEndingVerySoon) {
    t7 = isEndingHighlyUrgent && !isEndingVerySoon && canSubmit && <span className="tw-hidden tw-items-center tw-rounded-full tw-border tw-border-white/30 tw-bg-amber-600 tw-px-1.5 tw-py-0.5 tw-text-xs tw-font-medium tw-text-white xl:tw-inline-flex">Closing {endingCountdown}</span>;
    $[49] = canSubmit;
    $[50] = endingCountdown;
    $[51] = isEndingHighlyUrgent;
    $[52] = isEndingVerySoon;
    $[53] = t7;
  } else {
    t7 = $[53];
  }
  let t8;
  if ($[54] !== canSubmit || $[55] !== hasLimitedRemaining || $[56] !== isEndingVerySoon || $[57] !== waveInfo.participation.remainingSubmissions) {
    t8 = hasLimitedRemaining && !isEndingVerySoon && canSubmit && <span className="tw-hidden tw-items-center tw-rounded-full tw-border tw-border-white/30 tw-bg-primary-600 tw-px-1.5 tw-py-0.5 tw-text-xs tw-font-medium tw-text-white xl:tw-inline-flex">{waveInfo.participation.remainingSubmissions === 1 ? "Last One!" : `${waveInfo.participation.remainingSubmissions} Left`}</span>;
    $[54] = canSubmit;
    $[55] = hasLimitedRemaining;
    $[56] = isEndingVerySoon;
    $[57] = waveInfo.participation.remainingSubmissions;
    $[58] = t8;
  } else {
    t8 = $[58];
  }
  let t9;
  if ($[59] !== t5 || $[60] !== t6 || $[61] !== t7 || $[62] !== t8) {
    t9 = <div className="tw-flex tw-min-w-0 tw-items-center tw-gap-2 tw-whitespace-nowrap">{t5}{t6}{t7}{t8}</div>;
    $[59] = t5;
    $[60] = t6;
    $[61] = t7;
    $[62] = t8;
    $[63] = t9;
  } else {
    t9 = $[63];
  }
  let t10;
  if ($[64] !== handleMemesSubmit || $[65] !== t3 || $[66] !== t4 || $[67] !== t9 || $[68] !== tooltipText_3) {
    t10 = <PrimaryButton loading={false} disabled={t3} onClicked={handleMemesSubmit} padding="tw-px-2.5 tw-py-2" title={tooltipText_3}>{t4}{t9}</PrimaryButton>;
    $[64] = handleMemesSubmit;
    $[65] = t3;
    $[66] = t4;
    $[67] = t9;
    $[68] = tooltipText_3;
    $[69] = t10;
  } else {
    t10 = $[69];
  }
  return t10;
};
export default MyStreamWaveTabsMemeSubmit;